
import { useApiStore } from "@/stores/apiStore";
import { useAuthStore } from "@/stores/authStore";
import i18n from "@/utils/i18n";
import moment from "moment-timezone";
import { storeToRefs } from "pinia";
import { defineComponent, onMounted, Ref } from "vue";
import { useRouter } from "vue-router";
import InviteSSOUsers from "../Auth/InviteSSOUsers.vue";
import FilterDropdown from "./FilterDropdown.vue";

export default defineComponent({
	name: "imdex-manage-users",
	components: {
		InviteSSOUsers,
		FilterDropdown
	},
	setup() {
		const api = useApiStore();
		const authStore = useAuthStore();
		const router = useRouter();
		const { user }: { user: Ref<AuthUser | null> } = storeToRefs(authStore);
		onMounted(() => {
			const u = user.value; // eslint-disable-next-line
			if (
				u!.globalRole.name != "Administrator" &&
				u!.globalRole.name != "System Administrator"
			) {
				router.push("/ssa");
			}
		});

		return {
			api,
			moment,
			authStore,
			i18n
		};
	},
	data() {
		return {
			tableLoading: true,
			showDropdown: false,
			statusOptions: [
				{ label: "usersMgmt.inactive", value: "Inactive" },
				{ label: "usersMgmt.active", value: "Active" },
				{ label: "usersMgmt.pendingActivation", value: "Pending" }
			],
			auth0Options: [
				{ label: "userProfile.yes", value: "true" },
				{ label: "userProfile.no", value: "false" }
			],
			selectedFilters: {
				status: [] as string[],
				auth0: [] as string[]
			},
			filterLabels: {
				status: "usersMgmt.status",
				auth0: "userProfile.auth0User"
			},
			hasActiveFilters: false,
			// Vue doesn't allow computed properties in the data function
			// - we will explicitly recalculate these.
			// too many difficulties with reactivity!  will revert to string lookups
			activeFilters: [] as ("status" | "auth0")[],
			users: [] as AuthUser[],
			profilePicMap: {} as any,
			showInviteDialog: false
		};
	},
	async mounted() {
		await this.loadUsers();
	},
	methods: {
		async loadUsers() {
			this.tableLoading = true;
			const { data } = await this.api.get("/user/read");

			this.users = data;
			await this.getProfileImages(this.users);
			this.tableLoading = false;
		},
		async getProfileImages(users: AuthUser[]) {
			if (users.length) {
				users.forEach(async (user) => {
					if (user.profilePicUrl) {
						const response = await this.api.get(user.profilePicUrl, {
							responseType: "blob"
						});
						const blob = new Blob([response.data], { type: "image/png" });
						const objectUrl = URL.createObjectURL(blob);

						this.profilePicMap[user.profilePicUrl] = objectUrl;
						const profilePicElements = document.getElementsByClassName("profile-pic");

						let totalProfilePicElements = profilePicElements.length;
						for (const element of profilePicElements) {
							(element as HTMLImageElement).onload = () => {
								totalProfilePicElements--;

								if (!totalProfilePicElements) {
									for (const key in this.profilePicMap) {
										URL.revokeObjectURL(this.profilePicMap[key]);
									}
								}
							};
						}
					}
				});
			}
		},
		onInviteDialogClosed() {
			this.showInviteDialog = false;
			this.loadUsers();
		},
		updateFilter(category: "status" | "auth0", event: any) {
			if (!event || event.length === 0) {
				this.removeFilter(category);
			} else {
				this.selectedFilters[category] = event;
				if (this.activeFilters.indexOf(category) === -1) this.activeFilters.push(category);
			}
		},
		removeFilter(category: "status" | "auth0") {
			this.selectedFilters[category] = [];
			const index = this.activeFilters.indexOf(category);
			if (index > -1) {
				this.activeFilters.splice(index, 1);
			}
		},
		clearAllFilters() {
			this.activeFilters = [];
			this.selectedFilters = {
				status: [],
				auth0: []
			};
		}
	},
	computed: {
		auth0Label(): string {
			return this.$t("userProfile.auth0User");
		},
		statusLabel(): string {
			return this.$t("userProfile.status");
		},
		hasFilteredUsers(): boolean {
			return this.activeFilters.length > 0;
		},
		tableData(): { columns: string[]; tableData: AuthUser[] } {
			return {
				columns: ["name", "email", "isAuth0User", "lastLoggedIn", "isActive"],
				tableData: this.filteredUsers
			};
		},
		filteredUsers(): AuthUser[] {
			let users: AuthUser[] = this.users;
			if (this.selectedFilters.status.length > 0) {
				users = users.filter((user: AuthUser) => {
					const status =
						user.isActive === null ? "Pending" : user.isActive ? "Active" : "Inactive";
					return this.selectedFilters.status.includes(status);
				});
			}
			if (this.selectedFilters.auth0.length > 0) {
				users = users.filter((user: AuthUser) => {
					const isAuth0User = user.isAuth0User ? "true" : "false";
					return this.selectedFilters.auth0.includes(isAuth0User);
				});
			}
			return users;
		}
	}
});
